import Modal from 'react-modal';
import { usePost } from '../hooks/usePost';
import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import * as z from "zod";
import { useQuery } from '@tanstack/react-query';
import MultiSelectDropdown, { ArtistsSelect } from './artists-select';
import { SuccessResponse } from './form';

// ✅ Zod v4 schema with future date validation
const validationSchema = z.object({
  event_name: z
    .string()
    .min(3, { message: "Event name is required" })
    .refine(
      (val) => !/\b(sex|porn|xxx|nude|adult)\b/i.test(val),
      { message: "Event name contains inappropriate words" }
    ),
  club_name: z.string().min(2, { message: "Club is required" }),
  artist: z.string().min(2, { message: "Artist is required" }),
  city: z.string().min(2, { message: "City is required" }),
  contact_person_name: z.string().min(2, { message: "Contact person name is required" }),
  contact_person_email: z
    .email({ message: "Enter a valid email address" })
    .min(1, { message: "Contact email is required" }),
  contact_person_phone: z
    .string()
    .regex(/^\d+$/, { message: "Phone number must contain only digits" })
    .length(10, { message: "Phone number must be exactly 10 digits" })
    .refine(
      (val) => !/^(\d)\1{9}$/.test(val) && !/(0123456789|1234567890|9876543210)/.test(val),
      { message: "Enter a valid non-spam phone number" }
    ),
  date: z
    .string()
    .refine((val) => {
      if (!val) return false;
      const inputDate = new Date(val);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return inputDate > today;
    }, { message: "Event date must be in the future" }),
  time: z.string().min(1, { message: "Event time is required" }),
});


const EventSubmitForm = ({ isOpen, closeModal }) => {
   const [ response, setResponse ] = useState(null)

  const [formData, setFormData] = useState({
   artist_id: '123',
   artist: '',
   artists: []
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isValid, setIsValid] = useState(false);
  const [artistSearchTxt, setArtistSearchTxt] = useState('');
  const { data: artists, isLoading } = useQuery({
   queryKey: [ '/api/artists?q='+artistSearchTxt ]
  })

  function showSuccess({ message }) {
   setTimeout(() => {
      closeModal();
   }, 5000);
  }

  const { mutate, isPending } = usePost({
    url: '/api/submit-event',
    onSuccess: (res) => {
      // toast.success('Event submitted successfully');
      setResponse(res)
      showSuccess({
         message: res.message
      })
      setFormData({});
      setErrors({});
      setTouched({});
    },
    onError: (error) => {
      console.error('Error attending event:', error);
      toast.error("Something went wrong!");
    },
  });

  // ✅ validate all fields to enable/disable submit
  useEffect(() => {
    const result = validationSchema.safeParse(formData);
    setIsValid(result.success);
  }, [formData]);

  // ✅ Validate field on blur (Zod v4-compatible)
  const handleBlur = (name) => {
    const fieldSchema = validationSchema.shape[name];
    if (!fieldSchema) return;

    const value = formData[name] ?? "";
    const result = fieldSchema.safeParse(value);

    setTouched((prev) => ({ ...prev, [name]: true }));

    setErrors((prev) => ({
      ...prev,
      [name]: result.success
        ? undefined
        : result.error.issues?.[0]?.message || "Invalid input",
    }));
  };

  const handleFieldChange = (name) => (e) => {
    setFormData((prev) => ({ ...prev, [name]: e.target.value }));
    if (name == 'artist') {
      setArtistSearchTxt(e.target.value)
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const result = validationSchema.safeParse(formData);

    if (!result.success) {
      const fieldErrors = {};
      result.error.issues.forEach((issue) => {
        fieldErrors[issue.path[0]] = issue.message;
      });
      setErrors(fieldErrors);
      toast.error("Please fix the validation errors!");
      return;
    }

    setErrors({});
    let payload = new FormData(event.target)
    payload.set('artists[0][name]', formData.artist)
    payload.set('artists[0][_id]', formData.artist_id)
    mutate(payload);
  };

  return (
    <Modal
      isOpen={isOpen}
      className="event-attendance-modal"
      contentLabel="List your event form"
    >
      <h2>List your event</h2>
      <button onClick={closeModal} className="modal__close-btn">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-6">
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
        </svg>
      </button>
      {response ? (
         <>
         <SuccessResponse message={response.message} />
         </>
      ): (
         <form onSubmit={handleSubmit}>
            <input type="hidden" name="artist_id" value={formData.artist_id} />
           {[
             { name: 'event_name', type: 'text', placeholder: 'Name of the event' },
             { name: 'club_name', type: 'text', placeholder: 'Club' },
             { name: 'artist', type: 'text', placeholder: 'Artist' },
            //  { name: 'artists', type: 'text', placeholder: 'Artists' },
             { name: 'city', type: 'text', placeholder: 'City' },
             { name: 'contact_person_name', type: 'text', placeholder: 'Contact person name' },
             { name: 'contact_person_email', type: 'email', placeholder: 'Contact person email' },
             { name: 'contact_person_phone', type: 'tel', placeholder: 'Contact person phone' },
           ].map((field) => (
             <div className="mb-3 input-sec" key={field.name}>
               {field.type == 'custom-select' ? (
                  <MultiSelectDropdown apiUrl={'/api/artists'} selected={formData[field.name] || []} onChange={(val) => handleFieldChange({
                     target: {
                        name: 'artists',
                        value: val
                     }
                  })} />
               ): (
                  <input
                    value={formData[field.name] || ""}
                    onChange={handleFieldChange(field.name)}
                    onBlur={() => handleBlur(field.name)}
                    className="form-control"
                    type={field.type}
                    name={field.name}
                    placeholder={field.placeholder}
                    list={field.name == 'artist' ? 'artists_options': '#'}
                  />
               )}

               {}
               {touched[field.name] && errors[field.name] && (
                 <p className="text-danger text-sm">{errors[field.name]}</p>
               )}
             </div>
           ))}
           {isLoading ? 'Finding artists...': ''}
           {/* {JSON.stringify(artists)} */}
           <datalist id="artists_options">
            {artists?.data?.map((art, artkey) => (
               <option key={artkey} value={art.name}>
                  from {art.address?.city}
               </option>
            ))}
           </datalist>

           <div className="mb-3 input-sec">
             <div className="row">
               <div className="col-md-6">
                  <label htmlFor="date" className="form-control-label">Event Date</label>
                 <input
                   value={formData.date || ""}
                   onChange={handleFieldChange('date')}
                   onBlur={() => handleBlur('date')}
                   className="form-control"
                   type="date"
                   name="date"
                 />
                 {touched.date && errors.date && (
                   <p className="text-danger text-sm">{errors.date}</p>
                 )}
               </div>
               <div className="col-md-6">
                  <label htmlFor="date" className="form-control-label">Time</label>
                 <input
                   value={formData.time || ""}
                   onChange={handleFieldChange('time')}
                   onBlur={() => handleBlur('time')}
                   className="form-control"
                   type="time"
                   name="time"
                 />
                 {touched.time && errors.time && (
                   <p className="text-danger text-sm">{errors.time}</p>
                 )}
               </div>
             </div>
           </div>

           <div className='d-flex justify-content-between'>
            <button
               type='button'
               className={`btn btn-default`}
               onClick={closeModal}
            >
               Cancel
            </button>
            <button
               disabled={!isValid || isPending}
               className={`btn btn-dark  ${(!isValid || isPending) ? 'opacity-60 cursor-not-allowed' : ''}`}
            >
               {isPending ? 'Submitting...' : 'Submit'}
            </button>

           </div>
         </form>
      )}
    </Modal>
  );
};

export default EventSubmitForm;
